import { useMemo, useState } from "react";
import { MapFrame } from "@v0y4ge/jrni-ui";
import GoogleMap, { type GoogleMapMarker } from "../../components/maps/GoogleMap";

export interface LocationsMapItem {
  id: string;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
  selected?: boolean;
  onSelect?: () => void;
}

/** Shared multi-location map for public booking and other location pickers. */
export function LocationsMap({ locations, height = 240 }: { locations: LocationsMapItem[]; height?: number }) {
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const points = locations.filter((location): location is LocationsMapItem & { latitude: number; longitude: number } =>
    typeof location.latitude === "number" && Number.isFinite(location.latitude)
      && typeof location.longitude === "number" && Number.isFinite(location.longitude));
  const markers = useMemo<GoogleMapMarker[]>(() => points.map((point) => ({
    id: point.id,
    latitude: point.latitude,
    longitude: point.longitude,
    label: point.name,
    onClick: point.onSelect,
  })), [points.map((point) => `${point.id}:${point.latitude}:${point.longitude}:${point.selected ? 1 : 0}`).join("|")]);
  if (!points.length) return null;
  const center = {
    latitude: points.reduce((sum, point) => sum + point.latitude, 0) / points.length,
    longitude: points.reduce((sum, point) => sum + point.longitude, 0) / points.length,
  };
  return <MapFrame state={status} height={height} ariaLabel="Location map" loadingLabel="Loading map…" errorTitle="The map couldn’t load." errorDescription={error ?? "Google Maps could not be loaded."}>
    <GoogleMap
      apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
      mapId={import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID"}
      markers={markers}
      center={center}
      zoom={points.length === 1 ? 15 : 11}
      height={height}
      onStatusChange={(next, message) => { setStatus(next); setError(message ?? null); }}
    />
  </MapFrame>;
}

export default LocationsMap;